import { cn } from "@/lib/utils";
import { ModelConfig } from "@/lib/models";
import { useTranslation } from "@/lib/i18n";

interface ModelThumbnailProps {
  model: ModelConfig;
  selected: boolean;
  onClick: () => void;
}

const getModelIcon = (id: string) => {
  switch (id) {
    case "mug":
      return "☕";
    case "bass":
      return "🎸";
    case "lighter":
      return "🔥";
    default:
      return "📦";
  }
};

const getCategoryColor = (category: string) => {
  switch (category) {
    case "drinkware":
      return "from-amber-500/20 to-amber-600/5";
    case "instruments":
      return "from-purple-500/20 to-purple-600/5";
    case "accessories":
      return "from-orange-500/20 to-orange-600/5";
    default:
      return "from-primary/20 to-primary/5";
  }
};

export const ModelThumbnail = ({
  model,
  selected,
  onClick,
}: ModelThumbnailProps) => {
  const t = useTranslation();

  const getModelLabel = (id: string) => {
    switch (id) {
      case "mug":
        return t.models.coffeeMug;
      case "bass":
        return t.models.bassGuitar;
      case "lighter":
        return t.models.zippoLighter;
      default:
        return model.label;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "drinkware":
        return t.categories.drinkware;
      case "instruments":
        return t.categories.instruments;
      case "accessories":
        return t.categories.accessories;
      default:
        return category;
    }
  };

  return (
    <div
      className={cn(
        "group relative cursor-pointer transition-all duration-200",
        "flex-1 min-w-0",
      )}
      onClick={onClick}
    >
      <div
        className={cn(
          "aspect-square rounded-md border transition-all duration-200 relative overflow-hidden",
          "bg-gradient-to-br from-muted/40 to-muted/60 flex flex-col items-center justify-center p-2",
          selected
            ? "border-primary ring-1 ring-primary/30 shadow-sm scale-105 bg-primary/5"
            : "border-border/60 hover:border-primary/50 hover:shadow-sm group-hover:scale-[1.02]",
        )}
      >
        {/* Background pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />

        {/* Model icon with enhanced styling */}
        <div className="text-base mb-0.5 filter drop-shadow-sm relative z-10">
          {getModelIcon(model.id)}
        </div>

        {/* Model name with improved typography */}
        <div className="text-[0.55rem] font-medium text-center leading-tight text-foreground/90 relative z-10">
          {getModelLabel(model.id)}
        </div>

        {/* Selection indicator */}
        {selected && (
          <div className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-primary rounded-full flex items-center justify-center z-20">
            <div className="w-1 h-1 bg-white rounded-full" />
          </div>
        )}

        {/* Category-specific indicator */}
        <div className="absolute bottom-1 left-1 right-1 z-10">
          <div
            className={cn(
              "h-0.5 rounded-full bg-gradient-to-r",
              getCategoryColor(model.category),
            )}
          />
        </div>
      </div>

      <div className="mt-1 text-center">
        <div className="text-[0.5rem] text-muted-foreground leading-none">
          {getCategoryLabel(model.category)}
        </div>
      </div>
    </div>
  );
};
